/**
 * @license
 * Copyright (c) 2004 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { CalendarNodeType } from './CalendarUtils';
import { DateDef } from './ISODateTimezoneOffset';
import { FormatParseError, FormatParseErrorOptions } from '../UNSAFE_IntlFormatParse';
import { getISODateOffset } from './ISODateTimezoneOffset';
// import { Logger } from '../utils/UNSAFE_logger';
import { NativeDateTimeConstants } from './NativeDateTimeConstants';
import { NativeDateTimeOptions } from './NativeDateTimeOptions';
import { NativeDateTimeResolvedOptions } from './NativeDateTimeOptions';

import * as LocalOraI18nUtils from './dateTimeUtils';

interface ParsedObjectType {
  value: string;
  warning: string;
}

interface RangeType {
  name: string;
  value: number;
  low: number;
  high: number;
  displayValue: number;
  displayLow: number;
  displayHigh: number;
}
// cache locale system time zone
let localSystemTimeZone: string | null = null;

export class NativeParserImpl {
  public static parseImpl(
    str: string,
    pattern: string,
    resOptions: NativeDateTimeResolvedOptions,
    cal: CalendarNodeType
  ): ParsedObjectType {
    let dtStyle = 0;
    // First try if str is an iso 8601 string
    const testIsoStr = LocalOraI18nUtils._ISO_DATE_REGEXP.test(str);
    let parsedIsoStr = '';
    let isoStrInfo = null;
    let res: any;
    if (testIsoStr === true) {
      parsedIsoStr = str;
      dtStyle = this._isoStrDateTimeStyle(str);
    } else {
      dtStyle = this._dateTimeStyle(resOptions);
      res = this._parseExact(str, pattern, resOptions, cal);
      parsedIsoStr = res.value;
    }
    isoStrInfo = LocalOraI18nUtils.getISOStrFormatInfo(parsedIsoStr);
    if (resOptions.timeZone !== undefined && isoStrInfo.format !== NativeDateTimeConstants._LOCAL) {
      this._adjustHours(isoStrInfo, resOptions);
    }
    parsedIsoStr = this._createParseISOStringFromDate(dtStyle, isoStrInfo, resOptions);
    if (res === undefined) {
      res = { value: parsedIsoStr, warning: null };
    } else {
      res.value = parsedIsoStr;
      res.warning = null;
    }
    // If the returned ISO string is date-time and isoStrFormat is local,
    // log a warning saying that local is not supported for date-time iso strings
    // and return offset instead
    if (dtStyle === 2 && resOptions.isoStrFormat === NativeDateTimeConstants._LOCAL) {
      // Disable warning since it causes noise in test runs.
      // TODO: Re-enable once JET-59083 is fixed.
      //Logger.warn(
      //  'isoStrFormat was set to local for date-time ISO string. local was ignored and parse returned an ISO string with offset.'
      //);
    }
    return res;
  }
  // appends pre- and post- token match strings while removing escaped
  // characters.
  // Returns a single quote count which is used to determine if the
  // token occurs
  // in a string literal.
  private static _appendPreOrPostMatch(preMatch: string, strings: Array<string>): number {
    let quoteCount = 0;
    let escaped = false;
    for (let i = 0, il = preMatch.length; i < il; i++) {
      const c = preMatch.charAt(i);
      switch (c) {
        case "'":
          if (escaped) {
            strings.push("'");
          } else {
            quoteCount += 1;
          }
          escaped = false;
          break;
        case '\\':
          if (escaped) {
            strings.push('\\');
          }
          escaped = !escaped;
          break;
        default:
          strings.push(c);
          escaped = false;
          break;
      }
    }
    return quoteCount;
  }

  private static _validateRange(range: RangeType) {
    if (range.value < range.low || range.value > range.high) {
      const msg =
        range.displayValue +
        ' is out of range.  Enter a value between ' +
        range.displayLow +
        ' and ' +
        range.displayHigh +
        ' for ' +
        range.name;

      const errorInfo: FormatParseErrorOptions = {
        cause: {
          code: 'datetimeOutOfRange',
          parameterMap: {
            value: range.displayValue,
            minValue: range.displayLow,
            maxValue: range.displayHigh,
            propertyName: range.name
          }
        }
      };
      const rangeError = new (RangeError as any)(msg, errorInfo);
      throw rangeError;
    }
  }

  // Throw an exception if date-time pattern is invalid
  private static _throwInvalidDateFormat(
    format: string,
    options: NativeDateTimeOptions | NativeDateTimeResolvedOptions,
    m: string
  ) {
    const isDate =
      options.year !== undefined ||
      options.month !== undefined ||
      options.weekday !== undefined ||
      options.day !== undefined;
    const isTime =
      options.hour !== undefined || options.minute !== undefined || options.second !== undefined;

    let samplePattern = '';
    if (isDate && isTime) {
      samplePattern = 'MM/dd/yy hh:mm:ss a';
    } else if (isDate) {
      samplePattern = 'MM/dd/yy';
    } else {
      samplePattern = 'hh:mm:ss a';
    }

    const msg =
      'Unexpected character(s) ' +
      m +
      ' encountered in the pattern "' +
      format +
      ' An example of a valid pattern is "' +
      samplePattern +
      '".';
    const errorInfo: FormatParseErrorOptions = {
      cause: {
        code: 'optionValueInvalid',
        parameterMap: {
          propertyName: 'pattern',
          propertyValue: format
        }
      }
    };
    const error = new (SyntaxError as any)(msg, errorInfo);
    throw error;
  }
  // parse functions
  private static _throwWeekdayMismatch(weekday: string, day: number) {
    const msg = 'The weekday ' + weekday + ' does not match the date ' + day;
    const errorInfo: FormatParseErrorOptions = {
      cause: {
        code: 'dateToWeekdayMismatch',
        parameterMap: {
          weekday: weekday,
          date: day
        }
      }
    };
    const error = new FormatParseError(msg, errorInfo);
    throw error;
  }

  private static _throwDateFormatMismatch(value: string, format: string, style: number) {
    let msg = '';
    let errorCodeType = '';

    if (style === 2) {
      msg =
        'The value "' + value + '" does not match the expected date-time format "' + format + '"';
      errorCodeType = 'datetimeFormatMismatch';
    } else if (style === 0) {
      msg = 'The value "' + value + '" does not match the expected date format "' + format + '"';
      errorCodeType = 'dateFormatMismatch';
    } else {
      msg = 'The value "' + value + '" does not match the expected time format "' + format + '"';
      errorCodeType = 'timeFormatMismatch';
    }

    const errorInfo: FormatParseErrorOptions = {
      cause: {
        code: errorCodeType,
        parameterMap: {
          value: value,
          format: format
        }
      }
    };
    const error = new FormatParseError(msg, errorInfo);
    throw error;
  }

  private static _parseTimezoneOffset(_offset: string): Array<number> {
    const parts: Array<string> = _offset.split(':');
    const offsetParts = new Array<number>(2);

    // offset is +hh:mm
    if (parts.length === 2) {
      offsetParts[0] = parseInt(parts[0], 10);
      offsetParts[1] = parseInt(parts[1], 10);
    } else if (_offset.length === 2 || _offset.length === 3) {
      // offset is +hh or +h
      offsetParts[0] = parseInt(_offset, 10);
      offsetParts[1] = 0;
    } else {
      // offset is +hhmm
      offsetParts[0] = parseInt(_offset.substr(0, 3), 10);
      offsetParts[1] = parseInt(_offset.substr(3), 10);
    }
    return offsetParts;
  }

  private static _expandYear(start2DigitYear: number, year: number): number {
    // expands 2-digit year into 4 digits.
    year = Number(year);
    if (year < 100) {
      const ambiguousTwoDigitYear = start2DigitYear % 100;
      // eslint-disable-next-line no-param-reassign
      year += Math.floor(start2DigitYear / 100) * 100 + (year < ambiguousTwoDigitYear ? 100 : 0);
    }
    return year;
  }

  private static _arrayIndexOfMonthOrDay(
    monthsDaysData: any,
    userInput: string,
    type: 'months' | 'days'
  ): number {
    const trimUserInput = NativeParserImpl.toUpperTrimmedNoPeriod(userInput);
    const keys = Object.keys(monthsDaysData);
    for (let i = 0; i < keys.length; i++) {
      const valueFromData = monthsDaysData[keys[i]];
      const trimValueFromData = NativeParserImpl.toUpperTrimmedNoPeriod(valueFromData);
      // first try an exact match.
      if (trimUserInput === trimValueFromData) {
        return i;
      }

      // Test if user input is a number. We don't use parseInt because we want
      // to make sure trimUserInput is only number lie '5' and not '5abc'
      if (type === 'months') {
        const trimUserInputNumber = Number(trimUserInput);
        // In some locales the month name is something like 1月. Remove the literal
        // before compare in case the user enters only 1.
        const numberPartFromData = parseInt(trimValueFromData);
        if (Number.isInteger(numberPartFromData) && Number.isInteger(trimUserInputNumber)) {
          if (numberPartFromData === trimUserInputNumber) {
            return i;
          }
        }
      }
    }
    return -1;
  }

  private static toUpperTrimmedNoPeriod(value: string) {
    let trimItem = LocalOraI18nUtils.toUpper(value);
    trimItem = LocalOraI18nUtils.trim(trimItem);
    // In some locales the short month name ends with '.', remove it in case
    // the user does not enter the dot at the end of the month name.
    trimItem = trimItem.replace(/\.$/, '');
    return trimItem;
  }

  private static _getDayIndex(calNode: CalendarNodeType, value: string) {
    let ret = 0;
    let days = [];
    const calDaysFmt = calNode.days.format;
    const calDaysStdAlone = calNode.days['stand-alone'];
    days = [
      calDaysFmt.abbreviated,
      calDaysFmt.wide,
      calDaysStdAlone.abbreviated,
      calDaysStdAlone.wide
    ];
    for (let m = 0; m < days.length; m++) {
      ret = this._arrayIndexOfMonthOrDay(days[m], value, 'days');
      if (ret !== -1) {
        return ret;
      }
    }
    return ret;
  }

  private static _getMonthIndex(calNode: CalendarNodeType, value: string) {
    let ret = -1;
    const calMonthsFmt = calNode.months.format;
    const calMonthsStdAlone = calNode.months['stand-alone'];
    const months = [
      calMonthsFmt.wide,
      calMonthsFmt.abbreviated,
      calMonthsStdAlone.wide,
      calMonthsStdAlone.abbreviated
    ];
    for (let m = 0; m < months.length; m++) {
      ret = this._arrayIndexOfMonthOrDay(months[m], value, 'months');
      if (ret !== -1) {
        return ret;
      }
    }
    return ret;
  }

  // converts a format string into a regular expression with groups that
  // can be used to extract date fields from a date string.
  // check for a cached parse regex.
  private static _getParseRegExp(format: string, options: NativeDateTimeResolvedOptions): any {
    //let re = {};
    // expand single digit formats, then escape regular expression
    //  characters.
    const expFormat = format.replace(NativeDateTimeConstants._ESCAPE_REGEXP, '\\\\$1');
    const regexp = ['^'];
    const groups = [];
    let index = 0;
    let quoteCount = 0;
    // iterate through each date token found.
    let match = NativeDateTimeConstants._TOKEN_REGEXP.exec(expFormat);
    while (match !== null) {
      const preMatch = expFormat.slice(index, match.index);
      index = NativeDateTimeConstants._TOKEN_REGEXP.lastIndex;

      // don't replace any matches that occur inside a string literal.
      quoteCount += this._appendPreOrPostMatch(preMatch, regexp);
      if (quoteCount % 2) {
        regexp.push(match[0]);
      } else {
        // add a regex group for the token.
        const m = match[0];
        let add = '';
        if ((NativeDateTimeConstants._PROPERTIES_MAP as any)[m] !== undefined) {
          add = (NativeDateTimeConstants._PROPERTIES_MAP as any)[m].regExp;
        } else {
          this._throwInvalidDateFormat(format, options, m);
        }
        if (add) {
          regexp.push(add);
        }
        groups.push(match[0]);
      }
      match = NativeDateTimeConstants._TOKEN_REGEXP.exec(expFormat);
    }
    this._appendPreOrPostMatch(expFormat.slice(index), regexp);
    regexp.push('$');

    // allow whitespace to differ when matching formats.
    const regexpStr = regexp.join('').replace(/\s+/g, '\\s+');
    const parseRegExp = {
      regExp: regexpStr,
      groups: groups
    };
    // cache the regex for this format.
    //re[format] = parseRegExp;
    return parseRegExp;
  }

  private static _getTokenIndex(arr: Array<any>, token: string): number {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i][token] !== undefined) {
        return i;
      }
    }
    return 0;
  }

  // time lenient parse
  private static _parseLenienthms(
    result: Date,
    timepart: string,
    format: string,
    dtype: number,
    calNode: CalendarNodeType
  ) {
    // hour, optional minutes and optional seconds
    (NativeDateTimeConstants._TIME_REGEXP as any).lastIndex = 0;
    let hour = 0;
    let minute = 0;
    let second = 0;
    let msec = 0;
    let idx;
    let match: any = NativeDateTimeConstants._TIME_REGEXP.exec(timepart);
    let range: RangeType;
    if (match === null) {
      this._throwDateFormatMismatch(timepart, format, dtype);
    }
    if (match[1] !== undefined) {
      hour = parseInt(match[1], 10);
    }
    if (match[2] !== undefined) {
      minute = parseInt(match[2], 10);
    }
    if (match[3] !== undefined) {
      second = parseInt(match[3], 10);
    }
    if (match[4] !== undefined) {
      msec = parseInt(match[4], 10);
    }

    (NativeDateTimeConstants._TIME_FORMAT_REGEXP as any).lastIndex = 0;
    match = NativeDateTimeConstants._TIME_FORMAT_REGEXP.exec(format);
    switch (match[0]) {
      case 'h':
        // Hour in am/pm (1-12)
        if (hour === 12) {
          hour = 0;
        }
        range = {
          name: 'hour',
          value: hour,
          low: 0,
          high: 11,
          displayValue: hour,
          displayLow: 1,
          displayHigh: 12
        };
        this._validateRange(range);
        idx = this._matchPMSymbol(calNode, timepart);
        if (idx && hour < 12) {
          hour += 12;
        }
        break;
      case 'K':
        // Hour in am/pm (0-11)
        range = {
          name: 'hour',
          value: hour,
          low: 0,
          high: 11,
          displayValue: hour,
          displayLow: 0,
          displayHigh: 11
        };
        this._validateRange(range);
        idx = this._matchPMSymbol(calNode, timepart);
        if (idx && hour < 12) {
          hour += 12;
        }
        break;
      case 'H':
        range = {
          name: 'hour',
          value: hour,
          low: 0,
          high: 23,
          displayValue: hour,
          displayLow: 0,
          displayHigh: 23
        };
        this._validateRange(range);
        break;
      case 'k':
        if (hour === 24) {
          hour = 0;
        }
        range = {
          name: 'hour',
          value: hour,
          low: 0,
          high: 23,
          displayValue: hour,
          displayLow: 1,
          displayHigh: 24
        };
        break;
      default:
        break;
    }
    // Minutes.
    range = {
      name: 'minute',
      value: minute,
      low: 0,
      high: 59,
      displayValue: minute,
      displayLow: 0,
      displayHigh: 59
    };
    this._validateRange(range);
    // Seconds.
    range = {
      name: 'second',
      value: second,
      low: 0,
      high: 59,
      displayValue: second,
      displayLow: 0,
      displayHigh: 59
    };
    this._validateRange(range);
    // millisec
    range = {
      name: 'farctionalSecond',
      value: msec,
      low: 0,
      high: 999,
      displayValue: msec,
      displayLow: 0,
      displayHigh: 999
    };
    this._validateRange(range);
    result.setHours(hour, minute, second, msec);
  }

  private static _getWeekdayName(value: string, calNode: CalendarNodeType): string | null {
    const calDaysFmt = calNode.days.format;
    const calDaysStandAlone = calNode.days['stand-alone'];
    const days = [
      calDaysFmt.wide,
      calDaysFmt.abbreviated,
      calDaysStandAlone.wide,
      calDaysStandAlone.abbreviated
    ];

    for (let i = 0; i < days.length; i++) {
      const dayKeys = Object.keys(days[i]);
      for (let j = 0; j < dayKeys.length; j++) {
        const dName = (days as any)[i][dayKeys[j]];
        const dRegExp = new RegExp(dName + '\\b', 'i');
        if (dRegExp.test(value)) {
          return dName;
        }
      }
    }
    return null;
  }

  // lenient parse yMd and yMEd patterm. Must have year, moth,
  // date all numbers. Ex: 5/3/2013
  // weekday is optional. If present it must match date.
  // Ex:  Tuesday 11/19/2013
  // if year is 3-digits it can be anywhere in the string.
  // Otherwise assume its position based on pattern
  // if date > 12 it can be anywhere in the string.
  // Otherwise assume its position based on pattern
  // separators can be any non digit characters
  private static _parseLenientyMEd(
    value: string,
    format: string,
    options: NativeDateTimeResolvedOptions,
    cal: any,
    isDateTime: boolean
  ): ParsedObjectType {
    (NativeDateTimeConstants._YMD_REGEXP as any).lastIndex = 0;
    const match: any = NativeDateTimeConstants._YMD_REGEXP.exec(value);
    let dtype = 0;
    if (match === null) {
      dtype = isDateTime ? 2 : 0;
      this._throwDateFormatMismatch(value, format, dtype);
    }
    const tokenIndexes = [
      {
        y: format.indexOf('y')
      },
      {
        M: format.indexOf('M')
      },
      {
        d: format.indexOf('d')
      }
    ];
    tokenIndexes.sort(function (a, b) {
      const n1 = Object.keys(a)[0];
      const n2 = Object.keys(b)[0];
      return (a as any)[n1] - (b as any)[n2];
    });
    let year = 0;
    let month = 0;
    let day = 0;
    let yearIndex = 0;
    let foundDayIndex = 0;
    let i = 0;
    const dayIndex = this._getTokenIndex(tokenIndexes, 'd');
    let foundYear = false;
    let foundDay = false;

    for (i = 1; i <= 3; i++) {
      const tokenMatch: string = match[i];
      // find year if year is yyy|yyyy
      const intToken = parseInt(tokenMatch);
      if (tokenMatch.length > 2 || intToken > 31) {
        year = intToken;
        foundYear = true;
        yearIndex = i - 1;
      }
    }
    if (!foundYear) {
      yearIndex = this._getTokenIndex(tokenIndexes, 'y');
      year = match[this._getTokenIndex(tokenIndexes, 'y') + 1];
    }
    // find day if day value > 12
    for (i = 0; i < 3; i++) {
      if (i !== yearIndex && match[i + 1] > 12) {
        day = match[i + 1];
        foundDay = true;
        foundDayIndex = i;
        break;
      }
    }
    if (!foundDay) {
      if (yearIndex === this._getTokenIndex(tokenIndexes, 'd')) {
        day = match[this._getTokenIndex(tokenIndexes, 'y') + 1];
        month = match[this._getTokenIndex(tokenIndexes, 'M') + 1];
      } else if (yearIndex === this._getTokenIndex(tokenIndexes, 'M')) {
        day = match[this._getTokenIndex(tokenIndexes, 'd') + 1];
        month = match[this._getTokenIndex(tokenIndexes, 'y') + 1];
      } else {
        day = match[this._getTokenIndex(tokenIndexes, 'd') + 1];
        month = match[this._getTokenIndex(tokenIndexes, 'M') + 1];
      }
    } else {
      for (i = 0; i < 3; i++) {
        if (i !== foundDayIndex && i !== yearIndex) {
          month = match[i + 1];
          break;
        }
      }
      if (month === undefined) {
        month = match[this._getTokenIndex(tokenIndexes, 'M') + 1];
      }
    }
    month -= 1;
    const daysInMonth = LocalOraI18nUtils.getDaysInMonth(year, month);
    let range: RangeType;
    // if both month and day > 12 and swapped, throw exception
    // based on original order
    if (foundDay && dayIndex !== foundDayIndex && month > 12) {
      range = {
        name: 'month',
        value: day,
        low: 0,
        high: 11,
        displayValue: day,
        displayLow: 1,
        displayHigh: 12
      };
      this._validateRange(range);
    }
    range = {
      name: 'month',
      value: month,
      low: 0,
      high: 11,
      displayValue: month + 1,
      displayLow: 1,
      displayHigh: 12
    };
    this._validateRange(range);
    range = {
      name: 'day',
      value: day,
      low: 1,
      high: daysInMonth,
      displayValue: day,
      displayLow: 1,
      displayHigh: daysInMonth
    };
    this._validateRange(range);
    const start2DigitYear = options.twoDigitYearStart || 1950;
    year = this._expandYear(start2DigitYear, year);
    range = {
      name: 'year',
      value: year,
      low: 0,
      high: 9999,
      displayValue: year,
      displayLow: 0,
      displayHigh: 9999
    };
    this._validateRange(range);
    const parsedDate = new Date(year, month, day);
    // locate weekday
    const dName = this._getWeekdayName(value, cal);
    if (dName !== null) {
      const weekDay = this._getDayIndex(cal, dName);
      // day of week does not match date
      if (parsedDate.getDay() !== weekDay) {
        this._throwWeekdayMismatch(dName, parsedDate.getDate());
      }
    }
    if (isDateTime) {
      const timepart = value.substr(NativeDateTimeConstants._YMD_REGEXP.lastIndex);
      if (timepart.length === 0) {
        parsedDate.setHours(0, 0, 0, 0);
      } else {
        this._parseLenienthms(parsedDate, timepart, format, 2, cal);
      }
    }
    const result: ParsedObjectType = {
      value: LocalOraI18nUtils.dateToLocalIso(parsedDate),
      warning: 'lenient parsing was used'
    };
    return result;
  }

  // lenient parse yMMMd and yMMMEd patterns. Must have year, date as numbers
  // and month name.
  // weekday is optional. If present it must match date.
  // Ex:  Monday Nov, 11 2013
  // weekday and month name can be anywhere in the string.
  // if year > 2-digits it can be anywhere in the string.
  // Otherwise assume its position based on pattern
  // separators can be any non digit characters
  private static _parseLenientyMMMEd(
    value: string,
    format: string,
    options: NativeDateTimeResolvedOptions,
    cal: any,
    isDateTime: boolean
  ): ParsedObjectType {
    const origValue = value;
    // eslint-disable-next-line no-param-reassign
    value = LocalOraI18nUtils.toUpper(value);
    // locate month name
    const calMonthsFmt = cal.months.format;
    const calMonthsStandAlone = cal.months['stand-alone'];
    const months = [
      calMonthsFmt.wide,
      calMonthsFmt.abbreviated,
      calMonthsStandAlone.wide,
      calMonthsStandAlone.abbreviated
    ];
    let foundMatch = false;
    let reverseMonth = [];
    let i = 0;
    let mName = '';
    for (i = 0; i < months.length; i++) {
      reverseMonth = [];
      const monthKeys = Object.keys(months[i]);
      let j = 0;
      for (j = 0; j < monthKeys.length; j++) {
        mName = LocalOraI18nUtils.toUpper(months[i][monthKeys[j]]);
        reverseMonth.unshift({
          idx: j,
          name: mName
        });
      }

      reverseMonth.sort(function (a, b) {
        return b.idx - a.idx;
      });

      for (j = 0; j < reverseMonth.length; j++) {
        mName = reverseMonth[j].name;
        if (value.indexOf(mName) !== -1) {
          foundMatch = true;
          // eslint-disable-next-line no-param-reassign
          value = value.replace(mName, '');
          break;
        }
      }
      if (foundMatch) {
        break;
      }
    }
    // There is no month name. Try yMEd lenient parse.
    if (!foundMatch) {
      return this._parseLenientyMEd(origValue, format, options, cal, isDateTime);
    }

    const month = this._getMonthIndex(cal, mName);
    let range: RangeType = {
      name: 'month',
      value: month,
      low: 0,
      high: 11,
      displayValue: month,
      displayLow: 1,
      displayHigh: 12
    };
    this._validateRange(range);

    // locate weekday
    const dName = this._getWeekdayName(origValue, cal);
    const dRegExp = new RegExp(dName + '\\W', 'i');
    if (dName !== null) {
      // eslint-disable-next-line no-param-reassign
      value = value.replace(dRegExp, '');
    }
    // find year and date
    (NativeDateTimeConstants._YEAR_AND_DATE_REGEXP as any).lastIndex = 0;
    const match: any = NativeDateTimeConstants._YEAR_AND_DATE_REGEXP.exec(value);
    if (match === null) {
      const dtype = isDateTime ? 2 : 0;
      this._throwDateFormatMismatch(origValue, format, dtype);
    }
    const tokenIndexes = [
      {
        y: format.indexOf('y')
      },
      {
        d: format.indexOf('d')
      }
    ];

    tokenIndexes.sort(function (a, b) {
      const n1 = Object.keys(a)[0];
      const n2 = Object.keys(b)[0];
      return (a as any)[n1] - (b as any)[n2];
    });

    let year = 0;
    let day = 0;
    let yearIndex = 0;
    let foundYear = false;
    for (i = 1; i <= 2; i++) {
      const tokenMatch = match[i];
      // find year if year is yyy|yyyy
      const intToken = parseInt(tokenMatch);
      if (tokenMatch.length > 2 || intToken > 31) {
        year = intToken;
        foundYear = true;
        yearIndex = i - 1;
      }
    }
    if (!foundYear) {
      yearIndex = this._getTokenIndex(tokenIndexes, 'y');
      year = parseInt(match[this._getTokenIndex(tokenIndexes, 'y') + 1], 10);
    }
    if (yearIndex === this._getTokenIndex(tokenIndexes, 'd')) {
      day = parseInt(match[this._getTokenIndex(tokenIndexes, 'y') + 1], 10);
    } else {
      day = parseInt(match[this._getTokenIndex(tokenIndexes, 'd') + 1], 10);
    }

    const start2DigitYear = options.twoDigitYearStart || 1950;
    year = this._expandYear(start2DigitYear, year);
    range = {
      name: 'year',
      value: year,
      low: 0,
      high: 9999,
      displayValue: year,
      displayLow: 0,
      displayHigh: 9999
    };
    this._validateRange(range);
    const parsedDate = new Date(year, month, day);
    if (dName !== null) {
      const weekDay = this._getDayIndex(cal, dName);
      // day of week does not match date
      if (parsedDate.getDay() !== weekDay) {
        this._throwWeekdayMismatch(dName, parsedDate.getDate());
      }
    }
    const daysInMonth = LocalOraI18nUtils.getDaysInMonth(year, month);
    range = {
      name: 'day',
      value: day,
      low: 1,
      high: daysInMonth,
      displayValue: day,
      displayLow: 1,
      displayHigh: daysInMonth
    };
    this._validateRange(range);

    if (isDateTime) {
      const timepart = value.substr(NativeDateTimeConstants._YEAR_AND_DATE_REGEXP.lastIndex);
      if (timepart.length === 0) {
        parsedDate.setHours(0, 0, 0, 0);
      } else {
        this._parseLenienthms(parsedDate, timepart, format, 2, cal);
      }
    }
    const result: ParsedObjectType = {
      value: LocalOraI18nUtils.dateToLocalIso(parsedDate),
      warning: 'lenient parsing was used'
    };
    return result;
  }

  private static _parseLenient(
    value: string,
    format: string,
    options: NativeDateTimeResolvedOptions,
    cal: CalendarNodeType
  ): ParsedObjectType {
    const dtStyle = this._dateTimeStyle(options);
    let result: ParsedObjectType;
    switch (dtStyle) {
      case 0:
        // date style
        result = this._parseLenientyMMMEd(value, format, options, cal, false);
        break;
      case 1:
        // time style
        const d = new Date();
        this._parseLenienthms(d, value, format, 1, cal);
        const isoStr = LocalOraI18nUtils.dateToLocalIso(d);
        result = { value: isoStr, warning: 'lenient parsing was used' };
        break;
      case 2:
        // date-time style
        result = this._parseLenientyMMMEd(value, format, options, cal, true);
        break;
      default:
        result = { value: '', warning: 'lenient parsing was used' };
        break;
    }
    // convert result to gregorian
    const parts = LocalOraI18nUtils.IsoStrParts(result.value);
    const gregParts = [parts[0], parts[1], parts[2]];
    const isoParts = result.value.split('T');
    result.value =
      LocalOraI18nUtils.padZeros(gregParts[0], 4) +
      '-' +
      LocalOraI18nUtils.padZeros(gregParts[1], 2) +
      '-' +
      LocalOraI18nUtils.padZeros(gregParts[2], 2) +
      'T' +
      isoParts[1];
    return result;
  }

  private static _getNameIndex(
    calNode: CalendarNodeType,
    datePart: string,
    matchGroup: string,
    mLength: any,
    style: any,
    // @ts-ignore
    matchIndex: number,
    start1: number,
    end1: number,
    start2: string,
    end2: string,
    name: string
  ): number {
    let index = 0;
    const monthsFormat = (calNode as any)[datePart][style];
    if (datePart === 'months') {
      index = this._getMonthIndex(calNode, matchGroup);
    } else {
      index = this._getDayIndex(calNode, matchGroup);
    }
    const startName = monthsFormat[mLength][start2];
    const endName = monthsFormat[mLength][end2];
    const range = {
      name: name,
      value: index,
      low: start1,
      high: end1,
      displayValue: parseInt(matchGroup),
      displayLow: startName,
      displayHigh: endName
    };
    this._validateRange(range);
    return index;
  }

  private static _validateTimePart(
    matchInt: number,
    _timeObj: any,
    objMap: any,
    timeToken: string
  ) {
    const timeObj = _timeObj;
    timeObj[objMap.timePart] = matchInt;
    if (timeToken === 'h' || timeToken === 'hh') {
      if (matchInt === 12) {
        timeObj[objMap.timePart] = 0;
      }
    } else if (timeToken === 'k' || timeToken === 'kk') {
      timeObj.htoken = timeToken;
      if (matchInt === 24) {
        timeObj[objMap.timePart] = 0;
      }
    } else if (timeToken === 'K' || timeToken === 'KK') {
      if (matchInt === 12) {
        timeObj[objMap.timePart] = 0;
      }
    }
    const range: RangeType = {
      name: objMap.timePart,
      value: timeObj[objMap.timePart],
      low: objMap.start1,
      high: objMap.end1,
      displayValue: matchInt,
      displayLow: objMap.start2,
      displayHigh: objMap.end2
    };
    this._validateRange(range);
  }

  // test if the pattern/options is date, time or date-time
  // 0: date, 1:time, 2:date-time
  private static _dateTimeStyle(resOptions: NativeDateTimeResolvedOptions): number {
    const isTime =
      resOptions.hour !== undefined ||
      resOptions.minute !== undefined ||
      resOptions.second !== undefined ||
      resOptions.fractionalSecondDigits !== undefined;
    const isDate =
      resOptions.year !== undefined ||
      resOptions.month !== undefined ||
      resOptions.day !== undefined ||
      resOptions.weekday !== undefined;
    if (isDate && isTime) {
      return 2;
    } else if (isTime) {
      return 1;
    } else if (isDate) {
      return 0;
    }
    // try predefined style
    if (resOptions.dateStyle !== undefined && resOptions.timeStyle !== undefined) {
      return 2;
    }
    if (resOptions.timeStyle !== undefined) {
      return 1;
    }
    return 0;
  }

  // for zh-TW locales the pm symbol can vary depending on the time. We need
  // to iterate through the pm symbols in order to find a match.
  private static _matchPMSymbol(cal: CalendarNodeType, matchGroup: string) {
    const loc = cal.locale;
    let isPM = false;
    let i = 0;
    if (NativeDateTimeConstants._zh_tw_locales.includes(loc)) {
      const pmSymbols = NativeDateTimeConstants._zh_tw_pm_symbols;
      for (i = 0; i < pmSymbols.length; i++) {
        const pmSymbol = pmSymbols[i];
        if (matchGroup.indexOf(pmSymbol) !== -1) {
          return true;
        }
      }
    } else {
      const calPM = cal.dayPeriods.format.wide.pm;
      isPM = LocalOraI18nUtils.toUpper(matchGroup).indexOf(LocalOraI18nUtils.toUpper(calPM)) !== -1;
    }
    return isPM;
  }

  // exact match parsing for date-time. If it fails, try lenient parse.
  private static _parseExact(
    value: string,
    format: string,
    resOptions: NativeDateTimeResolvedOptions,
    cal: CalendarNodeType
  ): any {
    // remove spaces from era.
    const eraPart = cal.eras.eraAbbr['1'];
    const trimEraPart = LocalOraI18nUtils.trimNumber(eraPart);
    // eslint-disable-next-line no-param-reassign
    value = value.replace(eraPart, trimEraPart);
    const getOption = LocalOraI18nUtils.getGetOption(resOptions, 'NativeDateTimeConverter.parse');
    const lenientParse = getOption('lenientParse', 'string', ['none', 'full'], 'full');
    const dtStyle = this._dateTimeStyle(resOptions);

    // convert date formats into regular expressions with groupings.
    // use the regexp to determine the input format and extract the date
    //  fields.
    const parseInfo = this._getParseRegExp(format, resOptions);
    const match: any = new RegExp(parseInfo.regExp).exec(value);
    if (match === null) {
      if (lenientParse === 'full') {
        return this._parseLenient(value, format, resOptions, cal);
      }
      this._throwDateFormatMismatch(value, format, dtStyle);
    }
    // found a date format that matches the input.
    const groups = parseInfo.groups;
    let year = null;
    let month: any = null;
    let date = null;
    let weekDay = null;
    let hourOffset = '';
    let tzID = null;
    let pmHour = undefined;
    let weekDayName = '';
    let range: RangeType;
    const timeObj = {
      hour: 0,
      minute: 0,
      second: 0,
      millisec: 0,
      htoken: ''
    };
    const start2DigitYear = resOptions.twoDigitYearStart || 1950;
    // iterate the format groups to extract and set the date fields.
    for (let j = 0, jl = groups.length; j < jl; j++) {
      const matchGroup = match[j + 1];
      if (matchGroup) {
        const current = groups[j];
        const matchInt = parseInt(matchGroup, 10);
        const currentGroup = (NativeDateTimeConstants._PROPERTIES_MAP as any)[current];

        switch (currentGroup.token) {
          case 'months':
            month = this._getNameIndex(
              cal,
              currentGroup.token,
              matchGroup,
              currentGroup.mLen,
              currentGroup.style,
              currentGroup.matchIndex,
              0,
              11,
              '1',
              '12',
              'month name'
            );
            break;
          case 'days':
            weekDayName = matchGroup;
            weekDay = this._getNameIndex(
              cal,
              currentGroup.token,
              matchGroup,
              currentGroup.dLen,
              currentGroup.style,
              currentGroup.matchIndex,
              0,
              6,
              'sun',
              'sat',
              'weekday'
            );
            break;
          case 'time':
            this._validateTimePart(matchInt, timeObj, currentGroup, current);
            break;
          case 'dayOfMonth':
            date = matchInt;
            break;
          case 'monthIndex':
            // Month.
            month = matchInt - 1;
            // try lenient parse for date style only
            if (month > 11 && lenientParse === 'full') {
              try {
                return this._parseLenient(value, format, resOptions, cal);
              } catch (e) {
                range = {
                  name: 'month',
                  value: month,
                  low: 0,
                  high: 11,
                  displayValue: month + 1,
                  displayLow: 1,
                  displayHigh: 12
                };
                this._validateRange(range);
              }
            }
            break;
          case 'year':
            year = this._expandYear(start2DigitYear, matchInt);
            break;
          case 'ampm':
            pmHour = this._matchPMSymbol(cal, matchGroup);
            break;
          case 'tzhm':
            // Time zone hours minutes: -0800
            hourOffset = matchGroup.substr(-2);
            hourOffset = matchGroup.substr(0, 3) + ':' + hourOffset;
            break;
          case 'tzhsepm':
            // Time zone hours minutes: -08:00
            hourOffset = matchGroup;
            break;
          case 'tzh':
            // Time zone hours minutes: -08
            hourOffset = matchGroup + ':00';
            break;
          case 'tzid':
            // Time zone ID: America/Los_Angeles
            tzID = matchGroup;
            break;
          default:
            break;
        }
      }
    }
    const parsedDate = new Date();
    if (year === null) {
      year = parsedDate.getFullYear();
    }
    // if day and month are unspecified,the defaults are current
    // day and month.
    if (month === null && date === null) {
      month = parsedDate.getMonth();
      date = parsedDate.getDate();
    } else if (date === null) {
      // if day is unspecified, default 1st day of month.
      date = 1;
    }
    parsedDate.setFullYear(year, month, date);
    // validate month days
    const MonthDays = LocalOraI18nUtils.getDaysInMonth(year, month);
    range = {
      name: 'day',
      value: date,
      low: 1,
      high: MonthDays,
      displayValue: date,
      displayLow: 1,
      displayHigh: MonthDays
    };
    this._validateRange(range);
    // if pm designator token was found make sure the hours fit the
    // 24-hour clock.
    if (pmHour == true && timeObj.hour < 12) {
      timeObj.hour += 12;
    }
    // when hour is 12 AM and hour cycle is h24, set hour to 00
    if (
      pmHour == false &&
      timeObj.hour == 12 &&
      (timeObj.htoken == 'k' || timeObj.htoken == 'kk')
    ) {
      timeObj.hour = 0;
    }

    const parts = [year, month + 1, date];
    parts[3] = timeObj.hour;
    parts[4] = timeObj.minute;
    parts[5] = timeObj.second;
    parts[6] = timeObj.millisec;
    let isoParsedDate = LocalOraI18nUtils.partsToIsoString(parts);
    if (tzID !== null) {
      const zoneOffset = this._getTimeZoneOffset(parts, tzID);
      hourOffset = LocalOraI18nUtils.getTimeStringFromOffset('', zoneOffset, false, true);
    }
    if (hourOffset !== '') {
      isoParsedDate += hourOffset;
    }
    // validate year
    range = {
      name: 'year',
      value: year,
      low: 0,
      high: 9999,
      displayValue: year,
      displayLow: 0,
      displayHigh: 9999
    };
    this._validateRange(range);
    // validate month
    range = {
      name: 'month',
      value: month,
      low: 0,
      high: 11,
      displayValue: month + 1,
      displayLow: 1,
      displayHigh: 12
    };
    this._validateRange(range);
    // validate day range, depending on the month and year
    const daysInMonth = LocalOraI18nUtils.getDaysInMonth(parts[0], parts[1] - 1);
    range = {
      name: 'day',
      value: parts[2],
      low: 1,
      high: daysInMonth,
      displayValue: parts[2],
      displayLow: 1,
      displayHigh: daysInMonth
    };
    this._validateRange(range);
    // day of week does not match date
    if (weekDay !== null) {
      const validateDay = LocalOraI18nUtils.isoToLocalDate(isoParsedDate);
      if (validateDay && validateDay.getDay() !== weekDay) {
        this._throwWeekdayMismatch(weekDayName, validateDay.getDate());
      }
    }
    const result = { value: isoParsedDate };
    return result;
  }

  // test if the isoStr is date, time or date-time
  // 0: date, 1:time, 2:date-time
  private static _isoStrDateTimeStyle(isoStr: string): number {
    const timeIndex = isoStr.indexOf('T');
    if (timeIndex === -1) {
      return 0;
    }
    if (timeIndex > 0) {
      return 2;
    }
    return 1;
  }

  /*
   * Returns the offset of a time zone at a given moment-in-time.
   *
   * @param {Object} localeElements - locale data bundle, it also contain the
   * timezone data.
   * @param {string} tzName - The IANA time zone name for which we want to get the
   * offset. If it is the same as the local system's time zone, return the local
   * system's time zone offset by using Date.getTimezoneOffset() instead of
   * going through time zone data.
   * @param {array} parts - An array conataining year, month, day, hour, minute,
   * second that are used to create The Date object representing the moment-in-time.
   */
  private static _getTimeZoneOffset(parts: any, tzName: any) {
    // if The timeZone in the options is the same as local system's
    // timezone, use Date.getTimezoneOffset
    const localTtimeZone = this.getLocalSystemTimeZone();
    if (localTtimeZone === tzName) {
      const d = new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
      const localOffset = d.getTimezoneOffset();
      return -localOffset;
    }
    const dateParts: DateDef = {
      year: parts[0],
      month: parts[1],
      date: parts[2],
      hours: parts[3],
      minutes: parts[4]
    };
    const offset = getISODateOffset(dateParts, tzName);
    return offset;
  }

  private static _getAdjustedOffset(timezone: any, isoStrInfo: any) {
    const parts: Array<number> = isoStrInfo.isoStrParts;
    return this._getTimeZoneOffset(parts, timezone);
  }

  private static _adjustHours(isoStrInfo: any, options: NativeDateTimeResolvedOptions) {
    const value: Array<number> = isoStrInfo.isoStrParts;
    const isoStrFormat = isoStrInfo.format;
    let origOffset = 0;
    switch (isoStrFormat) {
      case NativeDateTimeConstants._OFFSET:
        const tzParts = this._parseTimezoneOffset(isoStrInfo.timeZone);
        const hoursOffset = tzParts[0];
        const minOffset = tzParts[1];
        origOffset = hoursOffset * 60 + (hoursOffset < 0 ? -minOffset : minOffset);
        break;
      case NativeDateTimeConstants._ZULU:
        origOffset = 0;
        break;
      default:
        break;
    }
    let newOffset = this._getAdjustedOffset(options.timeZone, isoStrInfo);
    newOffset -= origOffset;
    // target time zone might have switched to dst before orig timezone, or
    // orig time zone might have switched to dst before target timezone.
    // Need to get the correct offset at target time zone. This happens when
    // we are very close to after target or orig time zones switched to dst.
    const newDate = new Date(value[0], value[1] - 1, value[2], value[3], value[4], value[4]);
    newDate.setHours(value[3] + ((newOffset / 60) << 0), newOffset % 60);
    const newDateIso = LocalOraI18nUtils.dateToLocalIso(newDate);
    const newDateIsoStrInfo = LocalOraI18nUtils.getISOStrFormatInfo(newDateIso);
    newOffset = this._getAdjustedOffset(options.timeZone, newDateIsoStrInfo);
    newOffset -= origOffset;
    // Do the offset math through the Date object.
    const adjustD = new Date(
      Date.UTC(value[0], value[1] - 1, value[2], value[3], value[4], value[5])
    );
    const adjustedMin = adjustD.getUTCMinutes() + newOffset;
    // eslint-disable-next-line no-bitwise
    adjustD.setUTCHours(adjustD.getUTCHours() + ((adjustedMin / 60) << 0), adjustedMin % 60);
    value[0] = adjustD.getUTCFullYear();
    value[1] = adjustD.getUTCMonth() + 1;
    value[2] = adjustD.getUTCDate();
    value[3] = adjustD.getUTCHours();
    value[4] = adjustD.getUTCMinutes();
    value[5] = adjustD.getUTCSeconds();
  }

  // Returns a time-only, date-only or date-time ISO string based on dtStyle.
  private static _createISOStrParts(dtStyle: number, d: Array<any>): string {
    let ms: any = 0;
    let val = '';
    switch (dtStyle) {
      // Date only
      case 0:
        val =
          LocalOraI18nUtils.padZeros(d[0], 4) +
          '-' +
          LocalOraI18nUtils.padZeros(d[1], 2) +
          '-' +
          LocalOraI18nUtils.padZeros(d[2], 2);
        break;
      // Time only
      case 1:
        val =
          'T' +
          LocalOraI18nUtils.padZeros(d[3], 2) +
          ':' +
          LocalOraI18nUtils.padZeros(d[4], 2) +
          ':' +
          LocalOraI18nUtils.padZeros(d[5], 2);
        ms = d[6];
        if (ms > 0) {
          val += '.' + LocalOraI18nUtils.trimRightZeros(ms);
        }
        break;
      // Date-Time
      default:
        val =
          LocalOraI18nUtils.padZeros(d[0], 4) +
          '-' +
          LocalOraI18nUtils.padZeros(d[1], 2) +
          '-' +
          LocalOraI18nUtils.padZeros(d[2], 2) +
          'T' +
          LocalOraI18nUtils.padZeros(d[3], 2) +
          ':' +
          LocalOraI18nUtils.padZeros(d[4], 2) +
          ':' +
          LocalOraI18nUtils.padZeros(d[5], 2);
        ms = d[6];
        if (ms > 0) {
          val += '.' + LocalOraI18nUtils.trimRightZeros(ms);
        }
        break;
    }
    return val;
  }

  private static _getParseISOStringOffset(tzName: string, parts: Array<number>): string {
    const offset = this._getTimeZoneOffset(parts, tzName);
    return LocalOraI18nUtils.getTimeStringFromOffset('', offset, false, true);
  }

  private static _createParseISOStringFromDate(
    dtStyle: number,
    isoStrInfo: any,
    options: NativeDateTimeResolvedOptions
  ): string {
    const getOption = LocalOraI18nUtils.getGetOption(options, 'NativeDateTimeConverter.parse');
    const isoFormat = getOption(
      'isoStrFormat',
      'string',
      [
        NativeDateTimeConstants._ZULU,
        NativeDateTimeConstants._OFFSET,
        NativeDateTimeConstants._INVARIANT,
        NativeDateTimeConstants._LOCAL,
        NativeDateTimeConstants._AUTO
      ],
      NativeDateTimeConstants._AUTO
    );
    const parts = isoStrInfo.isoStrParts;
    const tzName = options.timeZone;
    let val = this._createISOStrParts(dtStyle, parts);
    // do not include timezone if date-only
    if (dtStyle === 0) {
      return val;
    }
    switch (isoFormat) {
      case NativeDateTimeConstants._OFFSET:
      case NativeDateTimeConstants._AUTO:
        val += this._getParseISOStringOffset(tzName, parts);
        break;
      case NativeDateTimeConstants._LOCAL:
        // if iso string is date-time, consider LOCAL as OFFSET
        if (dtStyle === 2) {
          val += this._getParseISOStringOffset(tzName, parts);
        }
        break;
      case NativeDateTimeConstants._ZULU:
        let adjustedMin = 0;
        adjustedMin = -this._getTimeZoneOffset(parts, tzName);
        if (adjustedMin !== 0) {
          // Do the offset math through date object.
          const adjustD = new Date(
            Date.UTC(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5], parts[6])
          );
          adjustedMin = adjustD.getUTCMinutes() + adjustedMin;
          // eslint-disable-next-line no-bitwise
          adjustD.setUTCHours(adjustD.getUTCHours() + ((adjustedMin / 60) << 0), adjustedMin % 60);
          parts[0] = adjustD.getUTCFullYear();
          parts[1] = adjustD.getUTCMonth() + 1;
          parts[2] = adjustD.getUTCDate();
          parts[3] = adjustD.getUTCHours();
          parts[4] = adjustD.getUTCMinutes();
          parts[5] = adjustD.getUTCSeconds();
          val = this._createISOStrParts(dtStyle, parts);
        }
        val += 'Z';
        break;
      default:
        break;
    }
    return val;
  }

  /*
   * Returns the current date at the specified timeZone. If timeZone is
   * null, return current date of the local system.
   */
  public static getTimeZoneCurrentDate(tzName: string | null): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      day: '2-digit',
      month: '2-digit'
    };
    if (tzName) {
      options.timeZone = tzName;
    }
    const cnv = Intl.DateTimeFormat('en-US', options);
    const fmt = cnv.format(new Date()); // fmt looks something like 03/05/2020
    const parts = fmt.split('/');
    const result = parts[2] + '-' + parts[0] + '-' + parts[1];
    return result;
  }

  /*
   * Returns the current offset of the specified timeZone.
   */
  public static getTimeZoneCurrentOffset(timezone: string): number {
    const d = new Date();
    const isoStr = LocalOraI18nUtils.dateToLocalIso(d);
    const isoStrInfo: any = LocalOraI18nUtils.getISOStrFormatInfo(isoStr);
    return this._getAdjustedOffset(timezone, isoStrInfo);
  }

  /*
   * Returns the local system's time zone.
   */
  public static getLocalSystemTimeZone(): string {
    if (!localSystemTimeZone) {
      const intlCnv = new Intl.DateTimeFormat('en-US');
      localSystemTimeZone = intlCnv.resolvedOptions().timeZone;
    }
    return localSystemTimeZone;
  }
}
